import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Module } from '../../modules/services/firmware/firmware.service';

@Injectable({
  providedIn: 'root',
})
export class ReleaseService {
  releasesUrl: string = environment.releases;

  constructor(private httpClient: HttpClient) {}

  // TODO: These should all be made into more RESTful API calls.
  createRelease(req: ReleaseRequest): Promise<void> {
    return this.httpClient
      .post<void>(`${this.releasesUrl}/releases`, req)
      .toPromise();
  }

  updateRelease(rel: Release): Promise<void> {
    return this.httpClient
      .put<void>(`${this.releasesUrl}/releases/${rel.id}`, rel)
      .toPromise();
  }

  getReleases(
    count?: number,
    paginationToken?: string,
  ): Promise<ReleaseResponse> {
    let url = `${this.releasesUrl}/releases`;
    const queryParams = [];
    if (count) {
      queryParams.push(`count=${count}`);
    }
    if (paginationToken) {
      queryParams.push(`paginationToken=${paginationToken}`);
    }
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    return this.httpClient.get<ReleaseResponse>(url).toPromise();
  }

  getRelease(id: string): Promise<Release> {
    return this.httpClient
      .get<Release>(`${this.releasesUrl}/releases/${id}`)
      .toPromise();
  }

  changeReleaseStatus(
    id: string,
    statusChangeRequest: StatusChangeRequest,
  ): Promise<void> {
    return this.httpClient
      .patch<void>(`${this.releasesUrl}/releases/${id}`, statusChangeRequest)
      .toPromise();
  }

  deployPhase(
    phaseName: string,
    id: string,
    requestCreator: string,
  ): Promise<void> {
    return this.httpClient
      .put<void>(`${this.releasesUrl}/releases/${id}/phase`, {
        phaseName,
        requestCreator,
      })
      .toPromise();
  }

  simulateRelease(req: SimulateRequest): Promise<SimulateResponse> {
    return this.httpClient
      .post<SimulateResponse>(`${this.releasesUrl}/releases/simulator`, req)
      .toPromise();
  }

  getReleaseChanges(releaseId: string): Promise<ReleaseChangeDetails> {
    return this.httpClient
      .get<ReleaseChangeDetails>(
        `${this.releasesUrl}/releases/${releaseId}/changes`,
      )
      .toPromise();
  }

  deleteRelease(releaseId: string, requestCreator: string): Promise<void> {
    return this.httpClient
      .delete<void>(
        `${this.releasesUrl}/releases/${encodeURIComponent(releaseId)}`,
        {
          body: { requestCreator: requestCreator },
        },
      )
      .toPromise();
  }

  approveOrRejectRelease(
    id: string,
    approver: string,
    status: string,
  ): Promise<void> {
    return this.httpClient
      .put<void>(`${this.releasesUrl}/releases/${id}/changes`, {
        approver,
        status,
      })
      .toPromise();
  }

  simulateChanges(releaseId: string, timestamp: Date): Promise<number> {
    return this.httpClient
      .post<number>(`${this.releasesUrl}/releases/changes/simulator`, {
        releaseId,
        timestamp,
      })
      .toPromise();
  }
}

export interface ReleaseRequest {
  name: string;
  description: string;
  releaseCreator: string;
  modules: Module[];
  phases: Phase[];
  firmware: ReleaseFirmwareVersion[];
}

export interface ReleaseResponse {
  releases: Release[];
  paginationToken: string;
  endOfSet: boolean;
  totalCount: number;
  releaseTotals: ModuleTotal[];
}

export interface ModuleTotal {
  moduleId: string;
  acceptedDeviceCount: number;
  impactedDeviceCount: number;
}

export interface Release {
  id: string;
  name: string;
  description: string;
  createdDate: string;
  deletedDate: string;
  status: string;
  releaseCreator: string;
  phases: Phase[];
  modules: Module[];
  firmware: ReleaseFirmwareVersion[];
  isRollingOut: string;
  releaseTotals?: ModuleTotal[];
  hasPendingApproval?: boolean;
  requestCreator?: string;
}

export interface ReleaseQuery {
  releaseName: string;
  releaseCreator: string;
  status: string[];
  appType: string[];
}

export interface ReleaseFirmwareVersion {
  build: string;
  version: string;
  name: string;
  firmwareId: string;
  platform: string;
  hardwarePlatform: string;
}

export interface ReleaseFirmwareVersion {
  build: string;
  version: string;
  name: string;
  firmwareId: string;
  platform: string;
  hardwarePlatform: string;
}

export interface Phase {
  name: string;
  expectedReleaseDate: string;
  cohorts: string[];
  description?: string;
  deploymentId?: string;
  status: string;
}

export interface DeployPhaseRequest {
  PhaseName: string;
}

export interface SimulateRequest {
  modules: Module[];
  phases: Phase[];
}

export interface SimulateResponse {
  potentialReleaseTotal: number;
  invalidReleaseEntities: ReleaseEntity[];
}

export interface ReleaseEntity {
  id: string;
  cohortName: string;
  type: string;
}

export interface StatusChangeRequest {
  status: string;
  requestCreator: string;
}

export interface ReleaseChangeDetails {
  releaseStatusChange: ReleaseStatusChange;
  moduleChanges: ModuleChange[];
  phaseChanges: PhaseChange[];
  firmwareChanges: FirmwareChange[];
  requestCreator: string;
  timestamp: Date;
  description: string;
  isDelete: boolean;
}

export interface PhaseChange {
  name: string;
  changeType: ChangeType;
  cohortsAdded: string[];
  cohortsRemoved: string[];
  currentDeploymentDate?: Date;
  proposedDeploymentDate?: Date;
  phase: Phase;
}

export interface ReleaseStatusChange {
  changeType: ChangeType;
  currentStatus: string;
  proposedStatus: string;
}

export interface ModuleChange {
  changeType: ChangeType;
  configurationChange: ConfigurationChange;
  versionChange: VersionChange;
  name: string; // Will be module name + platform in this use case
}

export interface VersionChange {
  currentVersion: string;
  proposedVersion: string;
}

export interface ConfigurationChange {
  currentConfiguration: string;
  proposedConfiguration: string;
}

export interface FirmwareChange {
  changeType: ChangeType;
  firmware: string;
}

export enum ChangeType {
  None = 'None',
  Add = 'Add',
  Remove = 'Remove',
  Edit = 'Edit',
}
